import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { authAPI } from '../utils/api';
import { Zap, Phone, ArrowRight, ChevronLeft, MessageCircle, Globe, Shield, RefreshCw } from 'lucide-react';
import './Auth.css';

// Country codes
const COUNTRIES = [
  { code: '+91', flag: '🇮🇳', name: 'India' },
  { code: '+1',  flag: '🇺🇸', name: 'USA' },
  { code: '+44', flag: '🇬🇧', name: 'UK' },
  { code: '+61', flag: '🇦🇺', name: 'Australia' },
  { code: '+971',flag: '🇦🇪', name: 'UAE' },
  { code: '+65', flag: '🇸🇬', name: 'Singapore' },
  { code: '+49', flag: '🇩🇪', name: 'Germany' },
  { code: '+33', flag: '🇫🇷', name: 'France' },
  { code: '+81', flag: '🇯🇵', name: 'Japan' },
  { code: '+86', flag: '🇨🇳', name: 'China' },
  { code: '+55', flag: '🇧🇷', name: 'Brazil' },
  { code: '+7',  flag: '🇷🇺', name: 'Russia' },
];

export default function LoginPage() {
  const [step, setStep]         = useState('phone'); // phone | otp | profile
  const [country, setCountry]   = useState(COUNTRIES[0]);
  const [phone, setPhone]       = useState('');
  const [otp, setOtp]           = useState(['','','','','','']);
  const [loading, setLoading]   = useState(false);
  const [resendTimer, setTimer] = useState(0);
  const [isNewUser, setNewUser] = useState(false); // eslint-disable-line
  const [fullPhone, setFullPhone]= useState('');
  const [devOtp, setDevOtp]     = useState('');
  const [profile, setProfile]   = useState({ displayName:'', username:'' });
  const [showCountry, setShowCountry] = useState(false);
  const otpRefs = useRef([]);
  const navigate  = useNavigate();
  const { login } = useAuth();
  const toast     = useToast();

  // Resend countdown
  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setInterval(() => setTimer(v => v - 1), 1000);
    return () => clearInterval(t);
  }, [resendTimer]);

  // Auto-focus first OTP box
  useEffect(() => {
    if (step === 'otp') setTimeout(() => otpRefs.current[0]?.focus(), 100);
  }, [step]);

  const handleSendOTP = async () => {
    if (!phone.trim()) { toast.error('Enter your phone number'); return; }
    const digits = phone.replace(/\D/g,'');
    if (digits.length < 7) { toast.error('Enter a valid phone number'); return; }

    setLoading(true);
    try {
      const full = `${country.code}${digits}`;
      setFullPhone(full);
      const { data } = await authAPI.sendOTP(full);
      setNewUser(data.isNewUser);
      if (data.otp) setDevOtp(data.otp); // dev mode OTP shown
      setStep('otp');
      setTimer(60);
      toast.success(`OTP sent to ${full}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send OTP');
    } finally { setLoading(false); }
  };

  const handleOtpChange = (idx, val) => {
    if (!/^\d*$/.test(val)) return;
    const newOtp = [...otp];
    newOtp[idx] = val.slice(-1);
    setOtp(newOtp);
    if (val && idx < 5) otpRefs.current[idx + 1]?.focus();
    if (newOtp.every(d => d)) handleVerifyOTP(newOtp.join(''));
  };

  const handleOtpKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !otp[idx] && idx > 0) {
      otpRefs.current[idx - 1]?.focus();
    }
    if (e.key === 'ArrowLeft'  && idx > 0) otpRefs.current[idx - 1]?.focus();
    if (e.key === 'ArrowRight' && idx < 5) otpRefs.current[idx + 1]?.focus();
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g,'').slice(0,6);
    if (pasted.length === 6) {
      const arr = pasted.split('');
      setOtp(arr);
      otpRefs.current[5]?.focus();
      handleVerifyOTP(pasted);
    }
  };

  const handleVerifyOTP = async (code) => {
    setLoading(true);
    try {
      const { data } = await authAPI.verifyOTP(fullPhone, code);
      if (data.isNewUser) {
        setNewUser(true);
        setProfile({ displayName: data.user.displayName || '', username: data.user.username || '' });
        // Store token temporarily
        localStorage.setItem('nexus_token', data.token);
        setStep('profile');
      } else {
        await login(null, null, data.token, data.user);
        toast.success(`Welcome back, ${data.user.displayName}!`);
        navigate('/home');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Wrong OTP. Try again.');
      setOtp(['','','','','','']);
      otpRefs.current[0]?.focus();
    } finally { setLoading(false); }
  };

  const handleCompleteProfile = async () => {
    if (!profile.displayName.trim()) { toast.error('Enter your name'); return; }
    if (!profile.username.trim())    { toast.error('Choose a username'); return; }
    if (!/^[a-zA-Z0-9_.]+$/.test(profile.username)) {
      toast.error('Username: letters, numbers, _ and . only'); return;
    }
    setLoading(true);
    try {
      const { data } = await authAPI.completeProfile(profile);
      await login(null, null, localStorage.getItem('nexus_token'), data);
      toast.success(`Welcome to Nexa, ${data.displayName}! 🎉`);
      navigate('/home');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save profile');
    } finally { setLoading(false); }
  };

  const handleResend = () => {
    if (resendTimer > 0) return;
    setOtp(['','','','','','']);
    setDevOtp('');
    handleSendOTP();
  };

  return (
    <div className="auth-page">
      {/* Left panel */}
      <div className="auth-left">
        <div className="auth-left-inner">
          <div className="auth-brand">
            <div className="auth-logo"><Zap size={24}/></div>
            <span>Nexa</span>
          </div>
          <h1 className="auth-headline">
            {step === 'phone'   && 'Connect with everyone, everywhere'}
            {step === 'otp'     && 'Verify your number'}
            {step === 'profile' && 'Set up your profile'}
          </h1>
          <p className="auth-desc">
            Private chats · Groups up to 1,000 · Communities · AI Assistant · Stories · Public Feed
          </p>
          <div className="auth-features">
            {[
              { icon: MessageCircle, color:'#6C63FF', label:'Private & encrypted messaging' },
              { icon: Globe,         color:'#00B4D8', label:'Public feed & community channels' },
              { icon: Shield,        color:'#00D68F', label:'End-to-end encrypted calls & chats' },
            ].map(f => { const Icon = f.icon; return (
              <div key={f.label} className="auth-feature">
                <div className="auth-feature-icon" style={{ background:f.color+'22', color:f.color }}><Icon size={17}/></div>
                <span>{f.label}</span>
              </div>
            );})}
          </div>
        </div>
        <div className="auth-left-glow g1"/><div className="auth-left-glow g2"/>
      </div>

      {/* Right panel */}
      <div className="auth-right">
        <div className="auth-card">

          {/* ── STEP 1: Phone ── */}
          {step === 'phone' && (
            <>
              <div className="auth-card-header">
                <div className="auth-logo-sm"><Phone size={18}/></div>
                <h2>Enter your phone number</h2>
                <p>We'll send you a one-time verification code</p>
              </div>

              {/* Country selector */}
              <div className="form-group">
                <label>Country</label>
                <div className="country-selector" onClick={() => setShowCountry(v=>!v)}>
                  <span className="country-flag">{country.flag}</span>
                  <span className="country-name">{country.name}</span>
                  <span className="country-code">{country.code}</span>
                  <ArrowRight size={14} style={{ marginLeft:'auto', opacity:0.5 }}/>
                </div>
                {showCountry && (
                  <div className="country-dropdown">
                    {COUNTRIES.map(c => (
                      <button key={c.code} className="country-option"
                        onClick={() => { setCountry(c); setShowCountry(false); }}>
                        <span>{c.flag}</span>
                        <span style={{ flex:1 }}>{c.name}</span>
                        <span style={{ color:'var(--text-muted)', fontSize:'0.8rem' }}>{c.code}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Phone Number</label>
                <div className="phone-input-row">
                  <span className="phone-prefix">{country.code}</span>
                  <input
                    type="tel" autoFocus
                    placeholder="9876543210"
                    value={phone}
                    onChange={e => setPhone(e.target.value.replace(/\D/g,''))}
                    onKeyDown={e => e.key === 'Enter' && handleSendOTP()}
                    maxLength={12}
                  />
                </div>
              </div>

              <button className="btn btn-primary auth-submit" onClick={handleSendOTP} disabled={loading || !phone.trim()}>
                {loading ? <span className="loader" style={{ width:18, height:18, borderWidth:2 }}/> : <><Send size={16}/> Send OTP</>}
              </button>

              <div className="auth-divider"><span>or</span></div>
              <p className="auth-alt-link">
                Use email instead? <Link to="/register">Register with email</Link>
              </p>
            </>
          )}

          {/* ── STEP 2: OTP ── */}
          {step === 'otp' && (
            <>
              <div className="auth-card-header">
                <button className="auth-back-btn" onClick={() => { setStep('phone'); setOtp(['','','','','','']); }}>
                  <ChevronLeft size={18}/>
                </button>
                <div className="auth-logo-sm" style={{ background:'linear-gradient(135deg,#00D68F,#00B4D8)' }}>
                  <Phone size={18}/>
                </div>
                <h2>Enter OTP</h2>
                <p>Sent to <strong>{fullPhone}</strong></p>
              </div>

              {/* Dev OTP helper */}
              {devOtp && (
                <div className="dev-otp-banner">
                  <span>🔧 Dev mode OTP:</span>
                  <strong style={{ letterSpacing:'0.15em', fontSize:'1.1rem' }}>{devOtp}</strong>
                  <button onClick={() => {
                    const arr = devOtp.split('');
                    setOtp(arr);
                    handleVerifyOTP(devOtp);
                  }}>Auto-fill</button>
                </div>
              )}

              <div className="otp-boxes" onPaste={handleOtpPaste}>
                {otp.map((digit, idx) => (
                  <input
                    key={idx}
                    ref={el => otpRefs.current[idx] = el}
                    type="tel" maxLength={1}
                    className={`otp-box ${digit ? 'filled' : ''}`}
                    value={digit}
                    onChange={e => handleOtpChange(idx, e.target.value)}
                    onKeyDown={e => handleOtpKeyDown(idx, e)}
                    disabled={loading}
                  />
                ))}
              </div>

              {loading && (
                <div style={{ textAlign:'center', padding:'12px 0', color:'var(--text-muted)', fontSize:'0.85rem' }}>
                  <span className="loader" style={{ width:20, height:20, borderWidth:2, display:'inline-block', marginRight:8 }}/>
                  Verifying...
                </div>
              )}

              <div className="otp-resend">
                {resendTimer > 0 ? (
                  <p>Resend OTP in <strong>{resendTimer}s</strong></p>
                ) : (
                  <button className="otp-resend-btn" onClick={handleResend}>
                    <RefreshCw size={14}/> Resend OTP
                  </button>
                )}
              </div>

              <p style={{ fontSize:'0.78rem', color:'var(--text-muted)', textAlign:'center', marginTop:12 }}>
                Didn't receive it? Check your SMS or tap Resend.
              </p>
            </>
          )}

          {/* ── STEP 3: Profile ── */}
          {step === 'profile' && (
            <>
              <div className="auth-card-header">
                <div className="auth-logo-sm" style={{ background:'linear-gradient(135deg,#FF4D8D,#6C63FF)' }}>
                  <Zap size={18}/>
                </div>
                <h2>Set up your profile</h2>
                <p>This is how others will see you on Nexa</p>
              </div>

              <div className="profile-avatar-pick">
                <div className="profile-avatar-circle">
                  {profile.displayName ? profile.displayName[0].toUpperCase() : '?'}
                </div>
                <p>Add a photo (optional)</p>
              </div>

              <div className="form-group">
                <label>Your Name *</label>
                <input autoFocus
                  placeholder="e.g. Vijay Kumar"
                  value={profile.displayName}
                  onChange={e => {
                    const name = e.target.value;
                    setProfile(p => ({
                      ...p,
                      displayName: name,
                      username: p.username || name.toLowerCase().replace(/\s+/g,'_').replace(/[^a-z0-9_.]/g,'').slice(0,20)
                    }));
                  }}
                  maxLength={60}
                />
              </div>
              <div className="form-group">
                <label>Username *</label>
                <div style={{ position:'relative' }}>
                  <span className="username-at">@</span>
                  <input
                    placeholder="vijay_kumar"
                    value={profile.username}
                    onChange={e => setProfile(p => ({ ...p, username: e.target.value.toLowerCase().replace(/[^a-z0-9_.]/g,'') }))}
                    maxLength={30}
                    style={{ paddingLeft:28 }}
                  />
                </div>
                <span style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>
                  Letters, numbers, _ and . only
                </span>
              </div>

              <button className="btn btn-primary auth-submit" onClick={handleCompleteProfile} disabled={loading || !profile.displayName.trim() || !profile.username.trim()}>
                {loading ? <span className="loader" style={{ width:18, height:18, borderWidth:2 }}/> : '🚀 Get Started'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Named import used in button icon
function Send({ size }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
    </svg>
  );
}
